import { Tr } from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import type { FlagMarginBelowEntry, PartType } from '../../hooks/useGetSettings';
import { useSettingsDraft } from '../../pages/settings/SettingsDraftContext';
import { Icon, IconButton, SegmentedControl } from '../../ui';
import {
    SettingsAddItemsModal,
    SettingsNumberField,
    SettingsOverridesBlock,
    SettingsSubTable,
    SettingsTd,
    SettingsTh,
    mergeEmptyOverrideRows,
    useEmptyOverrideRows,
    useSubTablePagination,
} from './primitives';

const PART_TYPE_OPTIONS: Array<{ value: PartType; label: string; }> = [
    { value: 'oem', label: 'OEM' },
    { value: 'oe-surplus', label: 'OE Surplus' },
    { value: 'a/m', label: 'Aftermarket' },
    { value: 'rcy', label: 'Recycled' },
    { value: 're-man', label: 'Remanufactured' },
    { value: 're-con', label: 'Reconditioned' },
];

const PART_TYPE_LABEL: Record<PartType, string> = Object.fromEntries(
    PART_TYPE_OPTIONS.map((o) => [o.value, o.label]),
) as Record<PartType, string>;

const DEFAULT_FLAG_MARGIN_ENTRY: FlagMarginBelowEntry = {
    partType: 'default',
    type: 'percentage',
    value: 0,
};

type ThresholdType = FlagMarginBelowEntry['type'];
type PartKey = FlagMarginBelowEntry['partType'];
type PendingTypeByPart = Partial<Record<PartKey, ThresholdType>>;
/** Threshold type chosen on an empty (uncommitted) part-type row. */
type EmptyRowTypeByPart = Partial<Record<PartType, ThresholdType>>;

/** Display-unit bound for Acceptable Margin values (±). Matches legacy SettingsRow limits. */
function flagMarginBelowLimit(type: ThresholdType): number {
    return type === 'percentage' ? 100 : 500;
}

function clampFlagMarginDisplayValue(type: ThresholdType, displayValue: number): number {
    const limit = flagMarginBelowLimit(type);
    return Math.min(limit, Math.max(-limit, displayValue));
}

function toStoredFlagMarginValue(type: ThresholdType, displayValue: number): number {
    const clamped = clampFlagMarginDisplayValue(type, displayValue);
    return type === 'percentage' ? clamped / 100 : clamped;
}

export interface FlagMarginBelowTableProps {
    title: string;
    description: string;
    /** Full Acceptable Margin list including the required `default` row. */
    entries?: Array<FlagMarginBelowEntry> | null;
    /**
     * Last-saved baseline (confirmed or server). Used to restore a row when
     * the user toggles threshold type back to the saved type without writing
     * a draft for a type-only click.
     */
    savedEntries?: Array<FlagMarginBelowEntry> | null;
    onChange: (next: Array<FlagMarginBelowEntry>) => void;
    modalTitle: string;
}

/**
 * Acceptable Margin Threshold editor: a non-removable Default row plus optional
 * per–part-type overrides. Shares SettingsOverridesBlock chrome with make
 * override tables so the two stay visually aligned.
 */
const FlagMarginBelowTable = ({
    title,
    description,
    entries: inputEntries,
    savedEntries: inputSavedEntries,
    onChange,
    modalTitle,
}: FlagMarginBelowTableProps) => {
    const { isDirty } = useSettingsDraft();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalSearchTerm, setModalSearchTerm] = useState('');
    const [pendingPartTypes, setPendingPartTypes] = useState<Set<PartType>>(new Set());
    // Type toggled away from the draft/saved row without a value yet — local
    // only so the unsaved-changes bar does not appear until the user types.
    const [pendingTypeByPart, setPendingTypeByPart] = useState<PendingTypeByPart>({});
    // Part types from "Add Part Type" that stay local until a Value is entered.
    const emptyRows = useEmptyOverrideRows<PartType>();
    const [emptyRowTypes, setEmptyRowTypes] = useState<EmptyRowTypeByPart>({});
    const entries = useMemo(
        () => (Array.isArray(inputEntries) ? inputEntries : []),
        [inputEntries],
    );
    const savedEntries = useMemo(
        () => (Array.isArray(inputSavedEntries) ? inputSavedEntries : []),
        [inputSavedEntries],
    );

    const savedByPart = useMemo(() => {
        const map = new Map<PartKey, FlagMarginBelowEntry>();
        for (const entry of savedEntries) {
            map.set(entry.partType, entry);
        }
        return map;
    }, [savedEntries]);

    const defaultEntry = useMemo(
        () => entries.find((e) => e.partType === 'default') ?? DEFAULT_FLAG_MARGIN_ENTRY,
        [entries],
    );

    const overrideRows = useMemo(
        () =>
            entries
                .filter((e): e is FlagMarginBelowEntry & { partType: PartType } => e.partType !== 'default')
                .slice()
                .sort((a, b) => PART_TYPE_LABEL[a.partType].localeCompare(PART_TYPE_LABEL[b.partType])),
        [entries],
    );

    // Discard/Save clear the draft without always changing entries identity
    // (e.g. type-only preview + another field edited). Drop pending previews then.
    useEffect(() => {
        if (!isDirty) setPendingTypeByPart({});
    }, [isDirty]);

    // When the margin list itself is rewritten (value commit / row revert),
    // drop pending types and prune empty rows now present in the draft.
    useEffect(() => {
        setPendingTypeByPart({});
    }, [entries]);

    useEffect(() => {
        emptyRows.pruneCommitted(overrideRows.map((r) => r.partType));
    }, [overrideRows, emptyRows]);

    useEffect(() => {
        setEmptyRowTypes((prev) => {
            const ids = new Set(emptyRows.emptyRowIds);
            let changed = false;
            const next = { ...prev };
            (Object.keys(prev) as Array<PartType>).forEach((partType) => {
                if (!ids.has(partType)) {
                    delete next[partType];
                    changed = true;
                }
            });
            return changed ? next : prev;
        });
    }, [emptyRows.emptyRowIds]);

    // Default first; draft overrides and uncommitted empty rows sorted together.
    const rows = useMemo(() => {
        const merged = mergeEmptyOverrideRows(
            overrideRows,
            emptyRows.emptyRowIds,
            (row) => row.partType,
            (partType) => ({
                partType,
                type: emptyRowTypes[partType] ?? 'percentage',
                value: 0,
            }),
        ).sort((a, b) =>
            PART_TYPE_LABEL[a.partType].localeCompare(PART_TYPE_LABEL[b.partType]),
        );
        return [defaultEntry, ...merged];
    }, [defaultEntry, overrideRows, emptyRows.emptyRowIds, emptyRowTypes]);

    const paging = useSubTablePagination(rows);

    const availablePartTypes = useMemo(() => {
        const taken = new Set([
            ...overrideRows.map((r) => r.partType),
            ...emptyRows.emptyRowIds,
        ]);
        return PART_TYPE_OPTIONS.filter((o) => !taken.has(o.value));
    }, [overrideRows, emptyRows.emptyRowIds]);

    const filteredModalPartTypes = useMemo(() => {
        const search = modalSearchTerm.toLowerCase();
        return availablePartTypes.filter((o) => o.label.toLowerCase().includes(search));
    }, [availablePartTypes, modalSearchTerm]);

    const clearPendingType = (partType: PartKey) => {
        setPendingTypeByPart((prev) => {
            if (!(partType in prev)) return prev;
            const next = { ...prev };
            delete next[partType];
            return next;
        });
    };

    const openModal = () => {
        setModalSearchTerm('');
        setPendingPartTypes(new Set());
        setIsModalOpen(true);
    };

    const togglePendingPartType = (partType: PartType) => {
        setPendingPartTypes((prev) => {
            const next = new Set(prev);
            if (next.has(partType)) next.delete(partType);
            else next.add(partType);
            return next;
        });
    };

    const handleAddPartTypes = () => {
        if (pendingPartTypes.size === 0) return;
        const added = Array.from(pendingPartTypes);
        emptyRows.addEmptyRows(added);
        setEmptyRowTypes((prev) => {
            const next = { ...prev };
            added.forEach((partType) => {
                if (next[partType] == null) next[partType] = 'percentage';
            });
            return next;
        });
        setIsModalOpen(false);
    };

    const updateEntry = (
        partType: FlagMarginBelowEntry['partType'],
        patch: Partial<FlagMarginBelowEntry>,
    ) => {
        const nextRows = [
            defaultEntry,
            ...overrideRows,
        ].map((entry) =>
            entry.partType === partType ? { ...entry, ...patch, partType } : entry,
        );
        const nextDefault = nextRows.find((e) => e.partType === 'default') ?? defaultEntry;
        const nextOverrides = nextRows.filter(
            (e): e is FlagMarginBelowEntry & { partType: PartType } => e.partType !== 'default',
        );
        onChange([nextDefault, ...nextOverrides]);
    };

    const handleValueChange = (
        row: FlagMarginBelowEntry,
        displayType: ThresholdType,
        displayValue: number,
    ) => {
        const stored = toStoredFlagMarginValue(displayType, displayValue);
        clearPendingType(row.partType);

        if (row.partType !== 'default' && emptyRows.hasEmptyRow(row.partType)) {
            emptyRows.removeEmptyRow(row.partType);
            setEmptyRowTypes((prev) => {
                if (!(row.partType in prev)) return prev;
                const next = { ...prev };
                delete next[row.partType as PartType];
                return next;
            });
            onChange([
                defaultEntry,
                ...overrideRows,
                { partType: row.partType, type: displayType, value: stored },
            ]);
            return;
        }

        updateEntry(row.partType, {
            type: displayType,
            value: stored,
        });
    };

    const handleTypeChange = (
        row: FlagMarginBelowEntry,
        displayType: ThresholdType,
        type: ThresholdType,
    ) => {
        if (type === displayType) return;

        if (row.partType !== 'default' && emptyRows.hasEmptyRow(row.partType)) {
            const partType = row.partType;
            setEmptyRowTypes((prev) => ({ ...prev, [partType]: type }));
            return;
        }

        const saved = savedByPart.get(row.partType);
        if (saved && type === saved.type) {
            clearPendingType(row.partType);
            updateEntry(row.partType, { type: saved.type, value: saved.value });
            return;
        }

        if (type === row.type) {
            clearPendingType(row.partType);
            return;
        }

        setPendingTypeByPart((prev) => ({ ...prev, [row.partType]: type }));
    };

    const handleRemoveRow = (partType: PartType) => {
        clearPendingType(partType);
        if (emptyRows.hasEmptyRow(partType)) {
            emptyRows.removeEmptyRow(partType);
            setEmptyRowTypes((prev) => {
                if (!(partType in prev)) return prev;
                const next = { ...prev };
                delete next[partType];
                return next;
            });
            return;
        }
        onChange([defaultEntry, ...overrideRows.filter((entry) => entry.partType !== partType)]);
    };

    const rowLabel = (partType: FlagMarginBelowEntry['partType']) =>
        partType === 'default' ? 'Default' : PART_TYPE_LABEL[partType];

    return (
        <>
            <SettingsOverridesBlock
                title={title}
                description={description}
                addButtonText="Add Part Type"
                onAddClick={openModal}
                isDisabled={availablePartTypes.length === 0}
            >
                <SettingsSubTable
                    isInset
                    currentPage={paging.currentPage}
                    totalPages={paging.totalPages}
                    onPageChange={paging.setCurrentPage}
                    head={
                        <>
                            <SettingsTh>Part Type</SettingsTh>
                            <SettingsTh>Value</SettingsTh>
                            <SettingsTh>Threshold Type</SettingsTh>
                            <SettingsTh textAlign="center" w="100px">
                                Actions
                            </SettingsTh>
                        </>
                    }
                >
                    {paging.pageItems.map((row) => {
                        const isEmptyRow =
                            row.partType !== 'default' && emptyRows.hasEmptyRow(row.partType);
                        const pendingType = pendingTypeByPart[row.partType];
                        const displayType = isEmptyRow
                            ? (emptyRowTypes[row.partType as PartType] ?? row.type)
                            : (pendingType ?? row.type);
                        const showEmptyValue = isEmptyRow || pendingType != null;
                        const limit = flagMarginBelowLimit(displayType);
                        const displayValue = showEmptyValue
                            ? null
                            : displayType === 'percentage'
                              ? Math.round(row.value * 100)
                              : row.value;
                        const label = rowLabel(row.partType);
                        const overridePartType = row.partType === 'default' ? null : row.partType;
                        return (
                            <Tr key={row.partType}>
                                <SettingsTd fontWeight="medium" color="fg.default">
                                    {label}
                                </SettingsTd>
                                <SettingsTd>
                                    <SettingsNumberField
                                        value={displayValue}
                                        onChange={(next) => {
                                            if (next == null) return;
                                            handleValueChange(row, displayType, next);
                                        }}
                                        allowEmpty={false}
                                        placeholder="0"
                                        min={-limit}
                                        max={limit}
                                        width="100px"
                                        prefix={displayType === 'dollar' ? '$' : undefined}
                                        unit={displayType === 'percentage' ? '%' : undefined}
                                        textAlign="right"
                                        ariaLabel={`${label} acceptable margin value`}
                                    />
                                </SettingsTd>
                                <SettingsTd>
                                    <SegmentedControl
                                        size="sm"
                                        value={displayType}
                                        onChange={(type) =>
                                            handleTypeChange(row, displayType, type)
                                        }
                                        segments={[
                                            { value: 'dollar', label: 'Dollar' },
                                            { value: 'percentage', label: 'Percent' },
                                        ]}
                                    />
                                </SettingsTd>
                                <SettingsTd textAlign="center">
                                    {overridePartType != null && (
                                        <IconButton
                                            aria-label={`Remove ${label}`}
                                            icon={<Icon name="trash" size="sm" />}
                                            size="sm"
                                            variant="ghost"
                                            colorScheme="red"
                                            onClick={() => handleRemoveRow(overridePartType)}
                                        />
                                    )}
                                </SettingsTd>
                            </Tr>
                        );
                    })}
                </SettingsSubTable>
            </SettingsOverridesBlock>

            <SettingsAddItemsModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                modalTitle={modalTitle}
                searchPlaceholder="Search part types..."
                searchTerm={modalSearchTerm}
                onSearchTermChange={setModalSearchTerm}
                emptySearchLabel={
                    modalSearchTerm ? 'No part types found' : 'All part types already listed'
                }
                items={filteredModalPartTypes.map((o) => ({
                    id: o.value,
                    label: o.label,
                }))}
                pendingIds={pendingPartTypes as Set<string>}
                onTogglePending={(id) => togglePendingPartType(id as PartType)}
                selectedCountLabel={
                    pendingPartTypes.size > 0
                        ? `${pendingPartTypes.size} part type${pendingPartTypes.size > 1 ? 's' : ''} selected`
                        : null
                }
                onAdd={handleAddPartTypes}
            />
        </>
    );
};

export default FlagMarginBelowTable;
